// API client de Consolidación — habla con el proxy de Orión (/orion/api/consolidation/*),
// que inyecta el token del modelo + org_id + reviewer SERVER-SIDE (nunca del
// navegador) y reenvía al cerebro soberano U-CogNet. Mismo patrón de auth
// (Bearer de localStorage) que memory-graph / client-radar.

const ORION = '/orion/api';
const BASE = '/consolidation';

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem('auth_token');
  return { Authorization: `Bearer ${token ?? ''}` };
}

// ── Tipos del contrato (GET /consolidation/cycle) ──
export type Verdict = 'approve' | 'reject' | 'needs_review' | 'defer';

// Una "unidad" de memoria: el cerebro la manda como objeto {statement,type,src,tier}
// (o, en payloads viejos, como string). Ver `unitStatement`/`unitSrc`/`unitTier`.
export type MemoryUnit = string | { statement?: string; type?: string; src?: string; tier?: string };

// Recomendación del modelo (ya viene en el ciclo; orienta al experto — la
// decisión final sigue siendo humana).
export interface Recommendation { verdict?: Verdict; rationale?: string }

// Checks jurídicos de un conflicto (true / false / null = indeterminado).
export interface SharedScope {
  same_subject?: boolean | null;
  same_fact_pattern?: boolean | null;
  same_time_scope?: boolean | null;
  same_territory?: boolean | null;
}

export interface Governance { decision_id?: string; allowed?: boolean }

export interface ChangeProposal {
  proposal_id: string;
  operation: string;              // p.ej. "merge_units" | "create_relation"
  affected_unit_ids?: string[];
  justification?: string;         // ya viene rica (2-3 frases)
  confidence?: number;            // 0–1
  risk_level?: string;            // low | medium | high
  requires_human_review?: boolean;
  reversible?: boolean;
  rollback_operation?: string;
  a?: MemoryUnit;                 // unidad A (objeto {statement,type,src,tier} o string)
  b?: MemoryUnit;                 // unidad B
  recommendation?: Recommendation;
  governance?: Governance;
  expert_verdict?: Verdict | null;
}

export interface ConflictCase {
  case_id: string;
  conflict_type?: string;
  classification?: string;        // potential_conflict | confirmed_conflict | indeterminate | …
  shared_scope?: SharedScope;
  prevails?: 'a' | 'b' | 'none' | string;
  reasoning_summary?: string;
  confidence?: number;
  requires_lawyer_review?: boolean;
  a?: MemoryUnit;
  b?: MemoryUnit;
  recommendation?: Recommendation;
  governance?: Governance;
  expert_verdict?: Verdict | null;
}

// Helpers para pintar una unidad (objeto {statement,type,src,tier} o string legacy).
export function unitStatement(u?: MemoryUnit): string {
  if (!u) return '';
  return typeof u === 'string' ? u : (u.statement || '');
}
export function unitSrc(u?: MemoryUnit): string {
  if (!u || typeof u === 'string') return '';
  return u.src || '';
}
export function unitTier(u?: MemoryUnit): string {
  if (!u || typeof u === 'string') return '';
  return u.tier || '';
}

export interface CycleSummary {
  cycle_id?: string;
  status?: string;
  units_evaluated?: number;
  changes_proposed?: number;
  changes_pending_review?: number;
  conflicts_detected?: number;
  merges_proposed?: number;
}

export interface ReviewProgress {
  reviewed: number;
  total: number;
  pct: number;
}

export interface CycleResponse {
  cycle_summary?: CycleSummary;
  metrics?: Record<string, unknown>;
  invariants?: Record<string, boolean>;
  review_progress?: ReviewProgress;
  change_proposals: ChangeProposal[];
  conflict_cases: ConflictCase[];
}

export interface DecideResult {
  ok?: boolean;
  proposal_id?: string;
  case_id?: string;
  verdict?: Verdict;
  [k: string]: unknown;
}

export class ConsolidationApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'ConsolidationApiError';
    this.status = status;
  }
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${ORION}${BASE}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...authHeaders(), ...(init?.headers || {}) },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const raw = (body as { detail?: unknown; error?: unknown }).detail ?? (body as { error?: unknown }).error;
    const msg = typeof raw === 'string' ? raw : raw ? JSON.stringify(raw) : `HTTP ${res.status}`;
    throw new ConsolidationApiError(msg, res.status);
  }
  return body as T;
}

export const consolidationApi = {
  // El ciclo completo de revisión (fusiones propuestas + conflictos). org_id lo
  // fija el proxy desde el JWT — no se manda desde el cliente.
  getCycle: () => req<CycleResponse>('/cycle'),

  // Veredicto experto. reviewer + org_id los inyecta el proxy (audit trail
  // confiable, no falsificable). El cliente manda el id + verdict + nota opcional.
  decide: (body: { proposal_id?: string; case_id?: string; verdict: Verdict; notes?: string }) =>
    req<DecideResult>('/decide', { method: 'POST', body: JSON.stringify(body) }),
};
